import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, PowerOff } from "lucide-react";
import { toast } from "sonner";
import {
  disableExtractionConfig,
  listExtractionConfigs,
  type ExtractionConfigSummary,
} from "@/api/extraction";
import { Page } from "@/components/page";
import { EmptyState } from "@/components/empty-state";

export function ExtractionListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const configs = useQuery({
    queryKey: ["extraction-configs"],
    queryFn: listExtractionConfigs,
  });

  const disable = useMutation({
    mutationFn: (c: ExtractionConfigSummary) => disableExtractionConfig(c.id),
    onSuccess: (updated) => {
      toast.success(`${updated.name} disabled`);
      qc.invalidateQueries({ queryKey: ["extraction-configs"] });
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const items = configs.data ?? [];

  return (
    <Page
      title="Extraction"
      actions={
        <button
          className="btn-primary"
          onClick={() =>
            navigate({
              to: "/extraction/$configId",
              params: { configId: "new" },
            })
          }
        >
          <Plus className="h-4 w-4" /> New config
        </button>
      }
    >
      <section className="card overflow-hidden">
        {items.length === 0 && !configs.isLoading ? (
          <EmptyState
            title="No extraction configs"
            hint="Auto-seed runs on api boot; if you're seeing this, the seed pass failed."
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-sm">
            <thead>
              <tr className="text-xs text-muted">
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Model</th>
                <th className="px-4 py-2 text-left font-medium">Verify model</th>
                <th className="px-4 py-2 text-left font-medium">Enabled</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-subtle/40">
                  <td className="px-4 py-2">
                    <Link
                      to="/extraction/$configId"
                      params={{ configId: c.id }}
                      className="text-accent hover:underline"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{c.model}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted">
                    {c.verify_model}
                  </td>
                  <td className="px-4 py-2">
                    {c.enabled ? (
                      <span className="badge-success text-[10px]">active</span>
                    ) : (
                      <span className="badge text-[10px] text-muted">off</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {c.enabled ? (
                      <button
                        className="btn-ghost text-xs"
                        onClick={() => {
                          if (confirm(`Disable config "${c.name}"?`)) {
                            disable.mutate(c);
                          }
                        }}
                        disabled={disable.isPending}
                        title="Soft-delete: sets enabled=false. Runs already in flight keep their pinned snapshot."
                      >
                        <PowerOff className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </Page>
  );
}
